// Message template engine for the "Send as me" action in Admin View-as-Participant mode.
// Templates support the following placeholders, which are substituted client-side
// before opening the native mail/SMS app:
//   {name}           — recipient's first name
//   {full_name}      — recipient's full name
//   {title}          — opportunity title
//   {date}           — formatted date (e.g., "Saturday, Mar 8, 2026")
//   {time}           — formatted start time (e.g., "9:00 AM") — empty if none
//   {time_range}     — formatted "start - end" range (e.g., "9:00 AM – 12:00 PM")
//   {location}       — opportunity location
//   {hours}          — numeric credited hours (e.g., "2.5")
import { format } from 'date-fns';
import { parseLocalDate, formatTimeRange } from './dateTime';

export const DEFAULT_TEMPLATES = {
  reminder: {
    subject: 'Friendly reminder: {title} on {date}',
    body: 'Hi {name},\n\nJust a friendly reminder about "{title}" on {date}{time_suffix} at {location}. Looking forward to seeing you there!\n\nThank you for being part of our community.',
  },
  thankyou: {
    subject: 'Thank you for volunteering at {title}',
    body: 'Hi {name},\n\nThank you so much for volunteering at "{title}" on {date}. Your time and kindness mean the world to us and to our community. We hope to see you again soon!',
  },
  followup: {
    subject: 'We missed you at {title}',
    body: 'Hi {name},\n\nWe missed you at "{title}" on {date} — no worries at all, life happens! We\'d love to have you at a future event. If you\'d like, reply to this message and we can help you find an opportunity that fits your schedule.',
  },
};

// Recommend a template key based on the signup status / whether it's past or upcoming.
export const recommendTemplateKey = (signup) => {
  if (!signup) return 'reminder';
  try {
    const oppDate = parseLocalDate(signup.opportunity_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPast = oppDate < today;
    if (!isPast) return 'reminder';
    if (signup.status === 'attended' || (typeof signup.hours_credited === 'number' && signup.hours_credited > 0)) return 'thankyou';
    return 'followup';
  } catch {
    return 'reminder';
  }
};

const firstName = (fullName) => (fullName || '').trim().split(/\s+/)[0] || '';

export const interpolate = (template, ctx) => {
  if (!template) return '';
  const timeRange = (ctx.start_time && ctx.end_time)
    ? formatTimeRange(ctx.start_time, ctx.end_time)
    : (ctx.start_time || '');
  const timeOnly = ctx.start_time ? formatTimeRange(ctx.start_time, null) : '';
  const timeSuffix = timeOnly ? ` at ${timeOnly}` : '';
  const dateStr = ctx.opportunity_date
    ? (() => { try { return format(parseLocalDate(ctx.opportunity_date), 'EEEE, MMM d, yyyy'); } catch { return ctx.opportunity_date; } })()
    : '';
  const replacements = {
    '{name}': firstName(ctx.participant_name),
    '{full_name}': ctx.participant_name || '',
    '{title}': ctx.opportunity_title || '',
    '{date}': dateStr,
    '{time}': timeOnly,
    '{time_range}': timeRange,
    '{time_suffix}': timeSuffix,
    '{location}': ctx.opportunity_location || '',
    '{hours}': (typeof ctx.hours_credited === 'number') ? String(ctx.hours_credited) : (ctx.hours_credited || ''),
  };
  return Object.entries(replacements).reduce(
    (str, [k, v]) => str.split(k).join(v),
    template
  );
};

// Resolve a template for a given key, preferring admin-customised templates
// (from /admin/settings) over built-in defaults.
export const resolveTemplate = (templateKey, savedSettings = {}) => {
  const def = DEFAULT_TEMPLATES[templateKey] || DEFAULT_TEMPLATES.reminder;
  return {
    subject: savedSettings[`${templateKey}_subject`] || def.subject,
    body: savedSettings[`${templateKey}_template`] || def.body,
  };
};
